<template>
  <div :class="['card', cardClass]">
    <div class="flex gap-3 justify-between mb-6 row-wrapper">
      <h3 class="page-header-2">{{ title }}</h3>
      <div
        :class="[
          'flex gap-3',
          hasMaxSizeText
            ? 'flex-col items-end md:flex-row md:items-center w-6/12 md:w-auto'
            : 'flex-row md:items-center'
        ]"
      >
        <slot name="header-extra" />
        <div class="whitespace-nowrap">
          <slot name="upload-button" />
        </div>
      </div>
    </div>
    <div class="media-grid">
      <MediaCard
        v-for="item in items"
        :key="item.id"
        :thumbnail-url="getThumbnailUrl(item)"
        :thumbnail-processed="item.thumbnail_processed ?? true"
        :name="item.name"
        :created-at="item.created_at"
        :customer-visible="item.customer_visible"
        :media-type="mediaType"
        class="max-w-[300px]"
        @click="openViewer(item)"
        @delete="handleDelete(item.id)"
        @toggle-visibility="handleVisibilityToggle(item)"
      />
    </div>
    <DeleteConfirmModal
      v-if="deleteConfirm"
      :item-type="itemTypeLabel"
      @confirm="onConfirmDelete"
      @cancel="closeDeleteConfirm"
    />
    <slot name="progress-modal" />
    <slot
      name="viewer-modal"
      :item="viewerItem"
      :close="closeViewer"
      :can-go-previous="canGoPrevious"
      :can-go-next="canGoNext"
      :go-previous="goToPrevious"
      :go-next="goToNext"
    />
  </div>
</template>

<script setup lang="ts" generic="T extends GalleryItem">
/* eslint-disable no-undef */
import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue";
import MediaCard, { type MediaType } from "@/components/MediaCard.vue";
import {
  type GalleryItem,
  type UseGalleryActionsOptions,
  useGalleryActions
} from "@/composables/useGalleryActions";

const props = withDefaults(
  defineProps<{
    title: string;
    items: T[];
    jobId: number | null;
    mediaType: MediaType;
    itemTypeLabel: string;
    cardClass?: string;
    hasMaxSizeText?: boolean;
    getThumbnailUrl?: (item: T) => string;
    galleryOptions: Omit<UseGalleryActionsOptions<T>, "items" | "jobId">;
  }>(),
  {
    cardClass: "photo-gallery",
    hasMaxSizeText: false,
    getThumbnailUrl: (item: T) =>
      (item as T & { thumbnail?: { url: string } }).thumbnail?.url || ""
  }
);

const emit = defineEmits<{
  "delete-success": [itemId: number];
}>();

const {
  deleteId,
  deleteConfirm,
  handleDelete,
  closeDeleteConfirm,
  confirmDelete,
  handleVisibilityToggle,
  viewerItem,
  openViewer,
  closeViewer,
  canGoPrevious,
  canGoNext,
  goToPrevious,
  goToNext
} = useGalleryActions<T>({
  items: () => props.items,
  jobId: () => props.jobId,
  mediaType: props.mediaType,
  ...props.galleryOptions
});

async function onConfirmDelete() {
  const itemId = await confirmDelete();
  if (itemId !== null) {
    emit("delete-success", itemId);
  }
}

defineExpose({
  deleteId,
  deleteConfirm,
  viewerItem,
  handleDelete,
  handleVisibilityToggle,
  closeDeleteConfirm,
  openViewer,
  closeViewer
});
</script>

<style scoped>
.media-grid {
  @apply p-0 grid gap-3;
  /* Responsive grid: minimum 150px per item, minimum 2 columns, items grow to fill space */
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(150px, calc(50% - 6px)), 1fr)
  );
}
</style>
